import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import * as api from './api';
import type { AuthUser, UserRole } from './api';
import { ROLE_LABEL } from './roleLabels';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (accessCode: string) => Promise<void>;
  logout: () => Promise<void>;
  /** 改我自己的名字 / 頭像顏色；改好後畫面上所有「我」的地方一起換 */
  updateProfile: (patch: { name?: string; avatarColor?: string }) => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // 這台電腦沒登入過（沒有登入狀態）→ 不用問後台：以前登入頁、邀請頁、分享連結每次打開都會多一個 401 失敗的請求
    if (!api.getSessionToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const u = await api.authMe();
      setUser(u);
    } catch (e) {
      // Dev fallback：本機 dev 連的 worker 還沒新 auth endpoint（404）
      // 直接假裝 admin 已登入，避免測既有功能時被 auth 卡住
      const status = (e as { status?: number }).status;
      const isLocalhost = typeof location !== 'undefined' && location.hostname === 'localhost';
      if (isLocalhost && (status === 404 || status === 405 || !status)) {
        setUser({
          id: 'u_phang',
          name: 'Phang', role: 'admin', avatarColor: '#10c78a',
        });
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (accessCode: string) => {
    const u = await api.authLogin(accessCode);
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try { await api.authLogout(); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (patch: { name?: string; avatarColor?: string }) => {
    const u = await api.updateMyProfile(patch);
    // 後台只回名字 / 顏色這些；浮水印用的 email 留著
    setUser(prev => (prev ? { ...prev, ...u } : u));
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, login, logout, updateProfile }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be inside <AuthProvider>');
  return v;
}

export function RequireAuth({ children, role }: { children: ReactNode; role?: UserRole }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="empty-state" role="status">
        <p>載入中…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (role && user.role !== role) {
    return (
      <div className="empty-state">
        <h1>403</h1>
        <p>沒有權限打開這個頁面（要{ROLE_LABEL[role]}才能看）</p>
      </div>
    );
  }
  return <>{children}</>;
}
